/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, useMemo} from 'react'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {
    Box,
    Heading,
    Text,
    Stack,
    Flex,
    Button,
    Grid,
    AspectRatio,
    Img,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Tabs, TabList, TabPanels, TabIndicator, Tab, TabPanel} from '@chakra-ui/react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import Link from '@salesforce/retail-react-app/app/components/link'
import {LeftArrow} from '../../components/icons'
import PropTypes from 'prop-types'
import {useCustomerOrders} from '../../hooks/use-customer-orders'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const EmptyOrderMessage = () => {
    return (
        <Stack data-testid="account-order-history-place-holder">
            <Heading fontSize="md" fontWeight="bold">
                <FormattedMessage id="account_order.heading" defaultMessage="NO ORDERS YET" />
            </Heading>
            <Text fontSize="md" fontWeight="normal">
                <FormattedMessage
                    id="account_order.empty.text"
                    defaultMessage="We don't have any past or current orders to display for you."
                />
            </Text>
        </Stack>
    )
}

const SkeletonPlaceholder = () =>
    Array.from({length: 4}).map((_, i) => (
        <Stack key={i} spacing={4} mb={4}>
            <Stack spacing={2}>
                <Skeleton h="20px" w="112px" />
                <Skeleton h="20px" w="200px" />
            </Stack>
            <Grid templateColumns="repeat(4, 140px)" gap={2} overflow="hidden">
                {Array.from({length: 4}).map((_, i) => (
                    <AspectRatio key={i} ratio={2 / 3} overflow="hidden">
                        <Skeleton boxSize="full" />
                    </AspectRatio>
                ))}
            </Grid>
            <Skeleton h="20px" w="200px" />
        </Stack>
    ))

// CSS-only responsive carousel constants
const GAP_SIZE = 8 // px
const SLIDES_MOBILE = 2.5
const SLIDES_DESKTOP = 6.5

// Responsive item widths calculated with CSS calc()
// Mobile: (100% - gaps) / 2.5 slides
// Desktop: (100% - gaps) / 6.5 slides
const ITEM_WIDTH_MOBILE = `calc((100% - ${(Math.floor(SLIDES_MOBILE) - 1) * GAP_SIZE}px) / ${SLIDES_MOBILE})`
const ITEM_WIDTH_DESKTOP = `calc((100% - ${(Math.floor(SLIDES_DESKTOP) - 1) * GAP_SIZE}px) / ${SLIDES_DESKTOP})`

const OrderProductImages = ({orderItems, isLoading, orderId}) => {
    // CSS-only horizontal scroll carousel - no Swiper/useBreakpointValue to avoid SSR/hydration issues

    if (isLoading) {
        return (
            <Flex gap={2} overflow="hidden">
                {Array.from({length: orderItems?.length || 3}, (_, i) => (
                    <Skeleton key={i} h="204px" w="136px" flexShrink={0} />
                ))}
            </Flex>
        )
    }

    return (
        <Box
            display="flex"
            gap={`${GAP_SIZE}px`}
            overflowX="auto"
            width="100%"
            sx={{
                // Hide scrollbar across browsers
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE/Edge
                '&::-webkit-scrollbar': {
                    display: 'none' // Chrome/Safari/Opera
                },
                // Smooth scrolling on touch devices
                WebkitOverflowScrolling: 'touch'
            }}
        >
            {orderItems?.map(({sku, productImageURL}, index) => (
                <Link
                    key={`${sku}-${index}`}
                    to={`/account/orders/${orderId}`}
                    flexShrink={0}
                    width={{base: ITEM_WIDTH_MOBILE, md: ITEM_WIDTH_DESKTOP}}
                >
                    <AspectRatio ratio={2 / 3} overflow="hidden">
                        <Img
                            alt={`Product ${sku}`}
                            src={productImageURL}
                            fallback={<Box background="gray.100" boxSize="full" />}
                        />
                    </AspectRatio>
                </Link>
            ))}
        </Box>
    )
}

OrderProductImages.propTypes = {
    orderItems: PropTypes.array,
    isLoading: PropTypes.bool
}

const AccountOrderHistory = () => {
    // Client-only rendering - skip SSR entirely to avoid hydration mismatches
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
        setHasMounted(true)
    }, [])

    const {currency} = useCurrency()
    const {data: customer} = useCurrentCustomer()
    const {customerId} = customer
    const [activeTab, setActiveTab] = useState(0)

    // Fetch both order types independently to avoid race conditions when switching tabs
    // Only fetch on client after mount
    const {
        data: onlineData,
        fetchNextPage: fetchNextOnline,
        isLoading: isLoadingOnline,
        isFetchingNextPage: isFetchingNextOnline,
        hasNextPage: hasNextOnline
    } = useCustomerOrders('online', hasMounted ? customerId : undefined)

    const {
        data: storeData,
        fetchNextPage: fetchNextStore,
        isLoading: isLoadingStore,
        isFetchingNextPage: isFetchingNextStore,
        hasNextPage: hasNextStore
    } = useCustomerOrders('store', hasMounted ? customerId : undefined)

    const onlineOrders = useMemo(
        () => onlineData?.pages?.flatMap(({orders = []}) => orders) ?? [],
        [onlineData?.pages]
    )

    const storeOrders = useMemo(
        () => storeData?.pages?.flatMap(({orders = []}) => orders) ?? [],
        [storeData?.pages]
    )

    const totalCounts = useMemo(
        () => ({
            online: onlineData?.pages[0]?.onlineOrdersLength || 0,
            store: onlineData?.pages[0]?.storeOrdersLength || 0
        }),
        [onlineData?.pages]
    )

    // Always show tab list - CSS handles responsive layout
    const showTabList = isLoadingOnline || totalCounts.online > 0 || totalCounts.store > 0

    const headingRef = useRef()
    useEffect(() => {
        headingRef?.current?.focus()
    }, [])

    // Extracted render logic to avoid code duplication between tabs
    // Each tab passes its own data and pagination handlers
    const renderOrdersList = (
        ordersList,
        tabIsLoading,
        tabFetchNextPage,
        tabIsFetchingNextPage,
        tabHasNextPage
    ) => {
        if (!ordersList?.length) {
            return null
        }

        return (
            <>
                {ordersList?.map(
                    ({
                        orderId,
                        orderDate,
                        orderStatus,
                        items: orderItems,
                        orderTotal,
                        isOnlineOrder,
                        storeInfo,
                        qtyProducts
                    }) => {
                        return (
                            <Stack key={orderId} spacing={2} mb="12">
                                <Link
                                    to={`/account/orders/${orderId}`}
                                    width="fit-content"
                                    _hover={{textDecoration: 'none'}}
                                >
                                    <Stack spacing={isOnlineOrder ? '6px' : 0}>
                                        <Text
                                            fontWeight="bold"
                                            fontSize="md"
                                            textTransform="uppercase"
                                        >
                                            {orderStatus}
                                        </Text>
                                        <Text fontSize="md">{orderDate}</Text>
                                        <Text>
                                            <FormattedNumber
                                                style="currency"
                                                currency={currency}
                                                value={orderTotal}
                                            />
                                        </Text>
                                        {!isOnlineOrder && storeInfo && (
                                            <Text fontSize="md">{storeInfo.name}</Text>
                                        )}
                                    </Stack>
                                </Link>

                                {isOnlineOrder && (
                                    <>
                                        <OrderProductImages
                                            isLoading={tabIsLoading}
                                            orderItems={orderItems}
                                            orderId={orderId}
                                        />
                                        <Stack
                                            direction={{base: 'column', lg: 'row'}}
                                            alignItems={{base: 'flex-start', lg: 'center'}}
                                            spacing={{base: '2px', lg: 2}}
                                        >
                                            <Text>
                                                <FormattedMessage
                                                    defaultMessage="{count, plural, one {# item} other {# items}}"
                                                    id="account_order_history.label.num_of_items"
                                                    description="Number of items in order"
                                                    values={{count: qtyProducts || orderItems?.length}}
                                                />
                                            </Text>
                                        </Stack>
                                    </>
                                )}

                                <Box>
                                    <Button
                                        as={Link}
                                        to={`/account/orders/${orderId}`}
                                        variant="link"
                                        fontSize="sm"
                                    >
                                        <FormattedMessage
                                            defaultMessage="View details"
                                            id="account_order_history.link.view_details"
                                        />
                                    </Button>
                                </Box>
                            </Stack>
                        )
                    }
                )}
                {tabHasNextPage && (
                    <Button
                        isLoading={tabIsLoading || tabIsFetchingNextPage}
                        variant="outline"
                        w={{base: 'full', lg: '250px'}}
                        onClick={() => tabFetchNextPage()}
                        textTransform="uppercase"
                    >
                        <FormattedMessage
                            id="account_order_history.load_more"
                            defaultMessage="Load More"
                        />
                    </Button>
                )}
            </>
        )
    }

    // SSR: render skeleton placeholder, no dynamic content
    // Client: render full content after mount
    if (!hasMounted) {
        return (
            <Stack spacing={4} data-testid="account-order-history-page" w="100%" maxW="100%" minW={0}>
                <Stack>
                    <Heading as="h1" fontSize="2xl">
                        <Flex alignItems="center" gap="64px" textTransform="uppercase">
                            <Box display={{base: 'inline-block', lg: 'none'}}>
                                <LeftArrow />
                            </Box>
                            <FormattedMessage
                                defaultMessage="Order History"
                                id="account_order_history.title.order_history"
                            />
                        </Flex>
                    </Heading>
                </Stack>
                <Skeleton h="42px" w="full" />
                <SkeletonPlaceholder />
            </Stack>
        )
    }

    return (
        <Stack spacing={4} data-testid="account-order-history-page" w="100%" maxW="100%" minW={0}>
            <Stack>
                <Heading as="h1" fontSize="2xl" tabIndex="0" ref={headingRef}>
                    <Flex alignItems="center" gap="64px" textTransform="uppercase">
                        <Link display={{base: 'inline-block', lg: 'none'}} href="/account">
                            <LeftArrow />
                        </Link>
                        <FormattedMessage
                            defaultMessage="Order History"
                            id="account_order_history.title.order_history"
                        />
                    </Flex>
                </Heading>
            </Stack>

            <Tabs index={activeTab} onChange={setActiveTab} variant="unstyled" isLazy>
                {/* Conditionally show TabList - hidden on mobile when no orders */}
                {showTabList &&
                    (isLoadingOnline ? (
                        <Skeleton h="42px" w="full" />
                    ) : (
                        <>
                            <TabList borderBottom="2px solid" borderColor="gray.50">
                                <Tab>
                                    <FormattedMessage
                                        defaultMessage="Online ({count})"
                                        id="account_order_history.tab.online_orders"
                                        values={{count: totalCounts.online}}
                                    />
                                </Tab>
                                <Tab>
                                    <FormattedMessage
                                        defaultMessage="Store ({count})"
                                        id="account_order_history.tab.store_orders"
                                        values={{count: totalCounts.store}}
                                    />
                                </Tab>
                            </TabList>
                            <TabIndicator mt="-2px" height="2px" bg="black" />
                        </>
                    ))}

                <TabPanels>
                    {/* Online Orders Tab Panel */}
                    <TabPanel px={0} py={6}>
                        {isLoadingOnline ? (
                            <SkeletonPlaceholder />
                        ) : (
                            <Stack gap={0}>
                                {renderOrdersList(
                                    onlineOrders,
                                    isLoadingOnline,
                                    fetchNextOnline,
                                    isFetchingNextOnline,
                                    hasNextOnline
                                )}
                            </Stack>
                        )}

                        {/* Tab-specific empty state for online orders */}
                        {!isLoadingOnline && !totalCounts.online && <EmptyOrderMessage />}
                    </TabPanel>

                    {/* Store Orders Tab Panel */}
                    <TabPanel px={0} py={6}>
                        {isLoadingStore ? (
                            <SkeletonPlaceholder />
                        ) : (
                            <Stack gap={0}>
                                {renderOrdersList(
                                    storeOrders,
                                    isLoadingStore,
                                    fetchNextStore,
                                    isFetchingNextStore,
                                    hasNextStore
                                )}
                            </Stack>
                        )}

                        {/* Tab-specific empty state for store orders */}
                        {!isLoadingStore && !totalCounts.store && <EmptyOrderMessage />}
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Stack>
    )
}

AccountOrderHistory.getTemplateName = () => 'account-order-history'

export default AccountOrderHistory
